// src/components/modals/ConfirmationModal.js
import React from 'react';
import { XMarkIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import ActionButton from '../common/ActionButton';
import { OptimizedImage } from '../../utils/imageManager';

const ConfirmationModal = ({ type, onClose, onConfirm, style, onMouseDown, cancelText, isLoading }) => {
  let title = '';
  let imageKey = '';
  let confirmText = '';
  let bodyText = null;
  let icon = null;
  let headerGradientFrom = '';
  let headerGradientTo = '';
  let headerAccentColor = '';

  if (type === 'skip') {
    title = 'Confirm Skip';
    imageKey = 'ACTION_SKIP';
    confirmText = 'Yes, Skip';
    bodyText = <p>Skipping an item marks it as learned and removes it from regular reviews.</p>;
    icon = <CheckCircleIcon className="h-5 w-5 text-white drop-shadow-sm" />;
    headerGradientFrom = 'var(--color-stat-reviewed)';
    headerGradientTo = 'var(--color-stat-reviewed-secondary)';
    headerAccentColor = 'var(--color-stat-reviewed)';
  } else if (type === 'delete') {
    title = 'Confirm Delete';
    imageKey = 'ACTION_INCORRECT';
    confirmText = 'Yes, Delete';
    bodyText = <p>This will permanently remove this item from your learning queue.</p>;
    icon = <TrashIcon className="h-5 w-5 text-white drop-shadow-sm" />;
    headerGradientFrom = 'var(--color-stat-streak)';
    headerGradientTo = 'var(--color-stat-streak-secondary)';
    headerAccentColor = 'var(--color-stat-streak)';
  } else {
    console.warn('Unsupported modal type:', type);
    return null;
  }

  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-md border border-border border-t-4 flex flex-col max-h-[90vh] sm:max-h-[80vh]"
      style={{
        borderTopColor: headerAccentColor,
        ...style,
      }}
      onMouseDown={onMouseDown}
    >
      {/* Glassmorphic header */}
      <div
        className="relative p-4 flex justify-between items-center overflow-hidden flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${headerGradientFrom}15, ${headerGradientTo}25)`,
        }}
      >
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />

        {/* Content */}
        <div className="relative z-10 flex items-center">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
            style={{
              background: headerAccentColor,
              boxShadow: `0 4px 12px ${headerAccentColor}30`,
            }}
          >
            {icon}
          </div>
          <h3 className="text-lg font-bold text-content-primary">{title}</h3>
        </div>

        <button onClick={onClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Content section */}
      <div className="flex-1 overflow-y-auto p-6 text-center">
        {/* Character image with glassmorphic background */}
        <div className="relative inline-block mb-4">
          <div
            className="
              w-28 h-28 rounded-full backdrop-blur-sm border border-border
              flex items-center justify-center overflow-hidden
            "
            style={{
              background: 'var(--color-background-primary)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Subtle glassmorphic overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-full" />
            <OptimizedImage 
              imageKey={imageKey} 
              className="relative z-10 w-20 h-20 object-contain" 
              fallback={<span className="text-4xl">😰</span>}
            />
          </div>
        </div>

        <div className="text-base text-content-secondary leading-relaxed">{bodyText}</div>
      </div>

      {/* Footer with glassmorphic buttons */}
      <div
        className="px-6 py-4 flex justify-end space-x-4 border-t border-border flex-shrink-0"
        style={{
          background: 'var(--color-background-primary)',
        }}
      >
        <ActionButton variant="glassy" onClick={onClose} color="neutral" className="w-full sm:w-auto px-4 py-2 text-white">
          {cancelText || (type === 'skip' ? 'No, Keep It' : type === 'delete' ? 'No, Keep It' : 'Cancel')}
        </ActionButton>

        <ActionButton
          variant="glassy"
          onClick={onConfirm}
          color={type === 'skip' ? 'yellow' : type === 'delete' ? 'red' : 'primary'}
          className="w-full sm:w-auto px-4 py-2 text-white"
          loading={isLoading}
        >
          {confirmText}
        </ActionButton>
      </div>
    </div>
  );
};

export default ConfirmationModal;
